import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import {
  FilterKey,
  FilterValuesObject,
  parseFilterValues,
  useAvailableFilters,
} from '@finn/ua-vehicle';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

type UseProductSliderToggles = {
  initialFilters: FilterValuesObject;
};
export type ProductSliderToggles = ReturnType<typeof useProductSliderToggles>;

const today = dayjs();
const in14 = today.add(14, 'day');
const DATE_IN_14_DAYS = in14.format('YYYY-MM-DD');

const DEFAULT_TOGGLE = 'beliebt';

export const useProductSliderToggles = ({
  initialFilters,
}: UseProductSliderToggles) => {
  const [filters, setFilters] = useState(initialFilters);
  const availableFilters = useAvailableFilters({
    filters: parseFilterValues(initialFilters),
  });

  const handleToggleClick = useCallback(
    (value: string) => {
      interactionTrackingEvent(TrackingEventName.FILTER_OPTION_CLICKED, {
        type: 'product-slider-toggle',
        value,
      });
      const [filterKey, filterValue] = value.split(':');
      if (!filterKey) return;

      setFilters(
        parseFilterValues({ ...initialFilters, [filterKey]: filterValue })
      );
    },
    [initialFilters]
  );

  const getSliderToggles = useCallback(() => {
    const toggles = [
      {
        text: 'Beliebt',
        value: DEFAULT_TOGGLE,
        isAvailable: true,
      },
      {
        text: 'SUVs',
        value: `${FilterKey.CAR_TYPES}:SUV`,
        isAvailable: availableFilters[FilterKey.CAR_TYPES]?.includes('SUV'),
      },
      {
        text: 'Elektro',
        value: `${FilterKey.FUELS}:Elektro`,
        isAvailable: availableFilters[FilterKey.FUELS]?.includes('Elektro'),
      },
      {
        text: 'Im Angebot',
        value: `${FilterKey.HAS_DEALS}:true`,
        isAvailable: true,
      },
      {
        text: 'Schnellste Lieferung',
        value: `${FilterKey.AVAILABLE_TO}:${DATE_IN_14_DAYS}`,
        isAvailable: availableFilters.availability.some((a) =>
          dayjs(a).isBefore(in14)
        ),
      },
      {
        text: 'Benzin',
        value: `${FilterKey.FUELS}:Benzin`,
        isAvailable: availableFilters[FilterKey.FUELS]?.includes('Benzin'),
      },
    ].filter(({ isAvailable }) => isAvailable);

    return {
      data: toggles,
      default: DEFAULT_TOGGLE,
    };
  }, [availableFilters]);

  return { getSliderToggles, handleToggleClick, filters };
};
